// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import {
  actividadDe,
  buscarCamion,
  declararRTO,
  estadoRTO,
  tractoresRTO,
} from "../services/porteria";
import AgendaRTO from "./AgendaRTO";
import CeldaVence from "../components/funcionales/CeldaVence";
import ConsultarCENT, {
  useConsultasCENT,
} from "../components/funcionales/ConsultarCENT";
import Ubicacion from "../components/funcionales/Ubicacion";
import Patente from "../components/funcionales/Patente";
import TablaUnidades from "../components/tablas/TablaUnidades";
import { useRealtime } from "../services/store";
import { ruta as rutaDeUnidad } from "../shared/unidad";
import {
  etiquetaCorta as empresaCorta,
  etiquetaCortaConDuda as empresaConDuda,
  nombreDe as empresaNombre,
} from "../shared/empresa";
import {
  FILTROS,
  conActividad,
  contar,
  cuando,
  etiquetaDe,
  fecha as fechaRTO,
  filtrar,
  puntoDe,
} from "../shared/rto";
import { sePuedeDeclarar, soloCampos } from "../shared/rtoDeclarada";
import { puedeConsultarCENT, sePuedeConsultar } from "../shared/rtoCent";
import Loading from "./Loading";
import usePolizas from "../hooks/usePolizas";
import { celdaSeguro, puedeVerSeguro } from "../shared/seguroFicha";

/* RTO de la flota: el portón y la lista del lunes.
 *
 * EL PROBLEMA: llega un chofer, ¿este camión puede salir? Hoy nadie lo sabe.
 * El vencimiento del RTO no estaba en ningún sistema — ni en el padrón, ni en
 * la planilla de vencimientos, ni en el bot. Se consultaba camión por camión
 * en la web del CENT, o no se consultaba.
 *
 * DOS PANTALLAS EN UNA, a propósito:
 *
 *   · Buscar    para el portón. Una patente, un veredicto grande, y listo.
 *   · Pendientes para la oficina. Todo lo que hay que renovar, ordenado por
 *     urgencia.
 *
 * La segunda es la que de verdad resuelve el problema: si alguien mira esa
 * lista una vez por semana, ningún camión llega al portón con el RTO vencido.
 * La primera es la red de contención para cuando igual pasa.
 */

/* Vencido hace más de esto ya no es una tarea de esta semana.
 *
 * La lista traía 463 camiones y arrancaba con uno vencido hace
 * 3749 días —diez años—. Una lista así no se usa: lo accionable queda enterrado
 * abajo de cosas que nadie va a resolver el lunes. Es la misma división que ya
 * hace la pantalla de furgones, con el mismo número. */
const DIAS_VIEJO = 90;


/* Lo que se acepta en `?vista=`. Una lista y no un `if`: una dirección con
 * una solapa inventada tiene que caer en la de siempre, no dejar la pantalla
 * sin dibujar nada. */
const VISTAS = new Set(["pendientes", "viejos", "buscar", "agenda"]);

/* ── POR QUÉ ESTA PANTALLA DEJÓ DE SER MASTER-DETAIL ─────────────────────
 *
 * Era una lista de botones a la izquierda y una ficha embebida a la derecha.
 * Nico pidió que tractores y furgones tuvieran LA MISMA vista, y que el click
 * en el interno abriera la ficha grande — así que la ficha de al lado se fue.
 *
 * NO SE PIERDE NADA, y se verificó una por una:
 *
 *   · el veredicto, las fechas, el turno y el historial → los dibuja
 *     `VeredictoRTO`, que es el MISMO componente que usa `/unidad/:tipo/:n`;
 *   · «sacarlo en la agenda» → sigue estando ahí, y ahora además SE LLEVA el
 *     interno y la patente puestos;
 *   · la última ubicación → ahora es una columna de la tabla, o sea que se ve
 *     de los 400 a la vez en vez de de a uno.
 *
 * Y se gana lo que el master-detail no podía dar: comparar. Una ficha por vez
 * contesta «¿este camión puede salir?»; la tabla contesta «¿cuáles hay que
 * renovar esta semana y de qué empresa son?», que es la pregunta del lunes y
 * la que esta pantalla decía en su propio encabezado que venía a resolver.
 *
 * ── Y NO ERA «EL RTO DE LOS TRACTORES»: MEZCLABA LAS DOS FLOTAS ─────────
 *
 * Salía de `rto_pendientes()`, que hace `union all` entre tractores y furgones.
 * Medido: **185 tractores y 295 furgones** — el 61% de las filas eran
 * acoplados, y acá nunca se filtró por `tipo`. Arrastraba tres cosas y ninguna
 * fallaba: esos 295 estaban TAMBIÉN en `/furgones-rto`; la tarjeta de la home
 * los contaba adentro del número de tractores y otra vez en la de acoplados; y
 * el enlace de la fila los mandaba a `/unidad/tractor/<interno>`, que con 430
 * internos repetidos abre el camión equivocado con total confianza.
 *
 * Ahora sale de `rto_tractores()`, la hermana de `rto_furgones()`: los 658, de
 * una sola tabla. `rto_pendientes()` queda como está porque su nombre dice la
 * verdad y el bot la lee así por WhatsApp.
 *
 * La solapa BUSCAR sigue yendo al servidor igual, y ya no por necesidad sino
 * porque busca en TODO el padrón —incluidas las bajas— y no sólo en lo que esta
 * pantalla tiene cargado.
 */

// -------------------------------------------------------------------- pantalla
const RTO = () => {
  /* LA SOLAPA SE PUEDE ENLAZAR (`?vista=agenda`), y no es un capricho: la
   * agenda de turnos vive acá y también hace falta desde el RTO de furgones,
   * que es otra ruta. Sin poder enlazarla, la única salida era dibujar una
   * segunda agenda allá — o sea dos pantallas para lo mismo, que es cómo se
   * termina cargando un turno donde nadie lo va a buscar.
   *
   * Se lee UNA VEZ, al montar, y de ahí manda el estado. Atado a la URL en
   * cada render, tocar una solapa tendría que reescribir la dirección y el
   * botón de atrás del navegador pasaría a recorrer solapas en vez de salir
   * de la pantalla. */
  const [params] = useSearchParams();
  const [vista, setVista] = useState(() =>
    VISTAS.has(params.get("vista")) ? params.get("vista") : "pendientes",
  );
  /* Y la unidad viaja con la solapa: `?vista=agenda&interno=774&patente=AH989GD`.
   *
   * Se lee UNA VEZ y al montar, igual que `?vista=`, por el mismo motivo: atado
   * a la URL en cada render, el alta pisaría lo que alguien esté tipeando.
   *
   * Que sea un enlace y no un estado compartido es lo que hace que funcione
   * DESDE OTRA RUTA — el botón vive en la ficha de `/unidad/:tipo/:n`, que es
   * otra pantalla. Es la misma decisión que ya había hecho enlazable la agenda
   * desde el RTO de furgones. */
  const [prefill] = useState(() => {
    const interno = params.get("interno") || "";
    const dominio = params.get("patente") || "";
    return interno || dominio ? { interno, dominio } : undefined;
  });
  /* TODOS los tractores, no sólo los pendientes. Antes salía de
   * `rto_pendientes()`, que hace `union all` con furgones: el 61% de las filas
   * eran acoplados —295 de 480— y la pantalla nunca filtró por `tipo`. Los
   * mismos furgones estaban además en `/furgones-rto`, y el enlace de la fila
   * los mandaba a `/unidad/tractor/<interno>`, que con 430 internos repetidos
   * abre el camión equivocado sin fallar. */
  const [pendientes, setPendientes] = useState(null);
  /* Cuándo se vio moverse a cada tractor, para el tag «Sin uso?». Va aparte y
   * no bloquea: si no llega, el tag no se enciende y la pantalla dice lo de
   * siempre. Ver `conActividad`, que además apaga la regla entera si las
   * fuentes de actividad están frías. */
  const [actividad, setActividad] = useState(null);
  const [error, setError] = useState(null);

  /* ── EL LÁPIZ: cargar la fecha que viene en el TÍTULO ───────────────────
   *
   * Un 0 km no rinde RTO por un año, así que el CENT no tiene nada suyo y esta
   * lista lo mostraba como «Sin RTO registrada» — igual que un camión al que
   * le falta el papel. Son **142 unidades activas** de la compra 2025/2026.
   *
   * VA APAGADO POR DEFECTO. Casi todo el que abre esta pantalla viene a MIRAR
   * qué hay que renovar; con los campos prendidos, una columna de 658 casillas
   * de fecha invita a tocar una sin querer. Misma regla que el lápiz de
   * Documentos. */
  const { permisos = null } = useOutletContext() || {};
  const puedeCargar = permisos === null || permisos.includes("cargar.documentos");
  const [lapiz, setLapiz] = useState(false);

  /* Después de guardar se vuelve a pedir ESA unidad y se le pegan los campos
   * encima, en su lugar. Recalcular el estado acá sería una segunda definición
   * de cuándo un camión puede salir; volver a traer las 658 haría saltar la
   * fila de posición justo cuando se están cargando de a 142 seguidas. */
  const guardarDeclarada = useCallback(async (dominio, vence, motivo) => {
    await declararRTO(dominio, vence, motivo);
    const fresco = await estadoRTO(dominio);
    setPendientes((fs) =>
      (fs || []).map((f) =>
        f.dominio === dominio ? { ...f, ...soloCampos(fresco) } : f,
      ),
    );
  }, []);

  const [texto, setTexto] = useState("");
  /* Arranca en «todos»: la solapa ya dejó adelante lo accionable, y abrir
   * además filtrado escondería filas sin que nadie lo haya pedido. */
  const [porEstado, setPorEstado] = useState("todos");
  const [resultados, setResultados] = useState([]);

  /* ── CONSULTAR EL CENT AHORA, de a una unidad ───────────────────────────
   *
   * Aldo, 09/10: «recién hicimos la VTV del furgón 448 y no aparece
   * habilitada en FIBOT». El dato era de la madrugada, de antes de la
   * revisión. Igna: lo que se puede resolver en el momento, se resuelve en el
   * momento (ticket #371). Cada fila con patente tiene su botón; la consulta
   * la hace el bot y lo que vuelve se pega en la fila, en su lugar —mismo
   * criterio que el lápiz: el estado lo decide `rto_estado()`, acá no se
   * recalcula nada—.
   *
   * Se pega también en las filas de BUSCAR: salen del padrón sin columnas de
   * RTO, y después de consultar sí las tienen. */
  const puedeConsultar = puedeConsultarCENT(permisos);
  /* La póliza de cada unidad (Aldo, 23/10, ticket 495). Con el mismo permiso
   * que la línea «Seguro» de la ficha; sin él, o si no se pudo leer, la
   * columna no está. */
  const verSeguro = puedeVerSeguro(permisos);
  const polizas = usePolizas(verSeguro);
  const pegarEstado = useCallback((dominio, fresco) => {
    const campos = soloCampos(fresco);
    const pegar = (fs) =>
      fs && fs.map((f) => (f.dominio === dominio ? { ...f, ...campos } : f));
    setPendientes(pegar);
    setResultados((fs) => pegar(fs) || []);
  }, []);
  const cent = useConsultasCENT(pegarEstado);

  /* El padrón, que ya está en memoria para los números del menú. `rto_tractores`
   * ya trae empresa y marca, pero el padrón tiene el resto; cruzar acá cuesta un
   * `Map` y no un viaje de red. Mismo criterio que el RTO de furgones. */
  const padron = useRealtime("tractores");
  const delPadron = useMemo(() => {
    const m = new Map();
    for (const x of padron || []) if (x?.id != null) m.set(String(x.id), x);
    return m;
  }, [padron]);

  useEffect(() => {
    tractoresRTO()
      .then(setPendientes)
      .catch((e) => setError(e.message || "No se pudo cargar"));
    /* Envuelto y con el rechazo tragado: el tag es un AGREGADO y no puede
     * tirar abajo la lista de RTO. `actividadDe` ya devuelve `null` si la
     * consulta falla; esto cubre además que ni siquiera devuelva una promesa. */
    Promise.resolve(actividadDe("tractor")).then(setActividad, () => {});
  }, []);

  /* LAS COLUMNAS SON LAS DE UN TRACTOR, y la vista es la misma que la de
   * furgones. No están lona, puerta ni tipo de acoplado: un tractor no tiene
   * ninguna de las tres, y dibujarlas vacías se leería como un dato que falta
   * cargar en vez de como una pregunta que no aplica — es la razón por la que
   * AdBlue no se dibuja en la ficha de un furgón.
   *
   * ESTADO NO LLEVA FILTRO DE COLUMNA: ya lo filtran los chips de arriba, que
   * además traen la cuenta. Con los dos se puede poner el chip en «Vencido» y
   * la columna en «Al día», ver cero filas y no entender por qué. */
  const columnas = useMemo(() => {
    const del = (c) => delPadron.get(String(c.interno)) || {};
    return [
      {
        clave: "interno",
        titulo: "Interno",
        /* LA PUERTA A LA FICHA GRANDE, que es lo que reemplazó al panel de al
         * lado. Sin interno no hay enlace: `/unidad/tractor/` no lleva a
         * ningún lado. */
        celda: (c) =>
          c.interno ? (
            <Link to={rutaDeUnidad("tractor", c.interno)} className="rto-link">
              {c.interno}
            </Link>
          ) : (
            "—"
          ),
      },
      {
        clave: "dominio",
        titulo: "Patente",
        /* Como chapa Mercosur, igual que en Acoplados (Igna, 03/10: «quedan
         * re facheras»), y con la misma excepción: en `sin_patente` el campo
         * tiene otra cosa copiada, y como chapa pasaría por una patente. */
        celda: (c) =>
          c.dominio && c.estado !== "sin_patente" ? (
            <Patente dominio={c.dominio} />
          ) : (
            c.dominio || "—"
          ),
        clase: () => "dato",
      },
      {
        clave: "empresa",
        titulo: "Empresa",
        filtro: true,
        /* El padrón guarda el CUIT. La sigla es lo que entra en la celda y el
         * nombre largo va en el `title`.
         *
         * El VALOR que se filtra es la sigla pelada y la CELDA lleva el signo
         * de pregunta de las que nadie confirmó (`IF`, `NOV`, `CEJAS`, ticket
         * 155). Si el filtro ofreciera «IF?» no emparejaría con el «IF» que
         * guarda el padrón, y elegirlo devolvería cero filas. */
        valor: (c) => empresaCorta(c.empresa ?? del(c).empresa),
        celda: (c) => {
          const v = c.empresa ?? del(c).empresa;
          return <span title={empresaNombre(v)}>{empresaConDuda(v)}</span>;
        },
        clase: (c) =>
          empresaCorta(c.empresa ?? del(c).empresa) === "—" ? "tenue" : undefined,
      },
      {
        clave: "marca",
        titulo: "Marca",
        filtro: true,
        valor: (c) => c.marca || del(c).marca || c.modelo || "—",
        clase: () => "tenue",
      },
      {
        clave: "estado",
        titulo: "Estado",
        /* `etiquetaDe` y no `ETIQUETA` directo: «Sin uso?» no sale del CENT
         * —sale de cruzar la vencida con la actividad— así que el servidor
         * nunca lo manda. Leyendo el estado crudo, filtrar por «Sin uso?»
         * mostraría una lista de filas que dicen «Vencido». */
        valor: (c) => etiquetaDe(c),
        celda: (c) => (
          <>
            <span className={"rto-punto rto-punto--" + puntoDe(c)} />
            {etiquetaDe(c)}
            {c.resultado && c.resultado !== "Apto" && (
              <strong className="rto-resultado"> · {c.resultado}</strong>
            )}
          </>
        ),
      },
      {
        clave: "vence",
        titulo: "Vence",
        /* El valor que se FILTRA sigue siendo la fecha que manda, prendido el
         * lápiz o no: con el filtro mirando otra cosa, elegir «12/03/2027» no
         * traería las filas que dicen 12/03/2027. */
        valor: (c) => fechaRTO(c.vence),
        celda: (c) => (
          <CeldaVence
            fila={c}
            editando={lapiz && puedeCargar}
            onGuardar={guardarDeclarada}
          />
        ),
      },
      {
        clave: "cuando",
        titulo: "Cuándo",
        valor: cuando,
        clase: (c) => (c.dias < 0 ? "rto-tarde" : undefined),
      },
      /* El seguro, al lado del RTO: son las dos preguntas de «¿puede salir?».
       * «Sin cargar» cuando no hay fila; «Sin seguro» sólo cuando Seguros lo
       * dijo (`estado` «baja», ver `seguroFicha`). */
      ...(verSeguro && polizas
        ? [
            {
              clave: "seguro",
              titulo: "Seguro",
              filtro: true,
              valor: (c) => celdaSeguro(polizas, c.dominio).valor,
              celda: (c) => celdaSeguro(polizas, c.dominio).texto,
              clase: (c) => {
                const s = celdaSeguro(polizas, c.dominio);
                return s.aviso ? "rto-tarde" : s.neutro ? "tenue" : undefined;
              },
            },
          ]
        : []),
      /* Al lado de «Cuándo», que es lo que se está mirando cuando hace falta:
       * el vencimiento que no se movió después de la revisión. Sin el permiso
       * la columna no está — vacía se leería como que falta un dato. */
      ...(puedeConsultar
        ? [
            {
              clave: "cent",
              titulo: "CENT",
              celda: (c) =>
                sePuedeConsultar(c) ? (
                  <ConsultarCENT
                    dominio={c.dominio}
                    consulta={cent.de(c.dominio)}
                    onConsultar={cent.consultar}
                  />
                ) : (
                  "—"
                ),
              clase: (c) => (sePuedeConsultar(c) ? undefined : "tenue"),
            },
          ]
        : []),
      {
        clave: "ubicacion",
        titulo: "Última ubicación registrada",
        filtro: true,
        valor: (c) => c.ubic_lugar || "—",
        celda: (c) => <Ubicacion fila={c} />,
      },
      {
        clave: "baja",
        titulo: "Unidad",
        filtro: true,
        /* LA BAJA SE MARCA Y NO SE ESCONDE. Un tractor dado de baja con el RTO
         * vencido no es trabajo pendiente, pero sacarlo de la lista haría que
         * los conteos cambien solos y nadie sepa por qué. Misma regla que el
         * toggle de baja del chofer y que el buscador. */
        valor: (c) =>
          (c.activo ?? del(c).activo) === false ? "Dada de baja" : "Activa",
        clase: (c) =>
          (c.activo ?? del(c).activo) === false ? "tenue" : undefined,
      },
    ];
  }, [delPadron, lapiz, puedeCargar, guardarDeclarada, puedeConsultar, cent,
      verSeguro, polizas]);

  const buscar = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      setResultados(await buscarCamion(texto));
    } catch (err) {
      setError(err.message || "No se pudo buscar");
    }
  };

  if (error && !pendientes) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }
  if (!pendientes) return <Loading />;

  /* Lo accionable arriba, lo arqueológico aparte. Un RTO vencido hace diez
     años no se resuelve esta semana: casi siempre es una unidad que ya no
     rueda y que nadie dio de baja. Siguen estando —se ven en su propia
     solapa y aparecen en la búsqueda—, pero no tapan lo que sí hay que
     hacer. */
  /* Las filas con la última actividad pegada. Las solapas siguen partiendo por
   * HACE CUÁNTO venció —que es otra pregunta— y el tag parte por hace cuánto
   * que no se la ve: una unidad puede estar vencida hace tres años y haber
   * rodado ayer, y ésas son justamente las que no hay que perder de vista. */
  const filas = conActividad(pendientes, actividad);
  const paraRenovar = filas.filter(
    (p) => !(p.estado === "vencida" && Math.abs(p.dias ?? 0) > DIAS_VIEJO),
  );
  const viejos = filas.filter(
    (p) => p.estado === "vencida" && Math.abs(p.dias ?? 0) > DIAS_VIEJO,
  );

  const deLaSolapa =
    vista === "pendientes"
      ? paraRenovar
      : vista === "viejos"
        ? viejos
        : resultados;
  /* Los conteos van sobre la SOLAPA y no sobre todo: un chip que dice
   * «Vencido · 463» arriba de una lista de 139 se lee como que filtró mal. */
  const cuentas = contar(deLaSolapa);
  /* Cuántas de la solapa esperan la fecha del título. Va sobre la SOLAPA, como
   * el resto de los conteos: un «✏️ · 142» arriba de una lista de 12 se lee
   * como que la pantalla filtró mal. */
  const porCargar = deLaSolapa.filter(
    (p) => sePuedeDeclarar(p) && !p.declarada,
  ).length;
  /* La búsqueda es su propia solapa acá, así que el filtro por estado no la
   * toca: quien escribió una patente preguntó por ese camión y por ninguno
   * más. */
  const lista = vista === "buscar" ? resultados : filtrar(deLaSolapa, porEstado);

  if (vista === "agenda") {
    return (
      <div className="tk tk--ancho">
        <div className="tk-filtros">
          <button className="tk-chip" onClick={() => setVista("pendientes")}>
            ← Vencimientos ({paraRenovar.length})
          </button>
          <button className="tk-chip activo">Agenda de turnos</button>
        </div>
        <AgendaRTO prefill={prefill} />
      </div>
    );
  }

  return (
    /* Ancho completo, como el RTO de furgones: ya no hay panel de al lado que
       necesite la mitad de la pantalla, y once columnas no entran en 380px. */
    <div className="rto-furgones">
      <div className="tk-filtros">
          <button
            className={"tk-chip" + (vista === "pendientes" ? " activo" : "")}
            onClick={() => setVista("pendientes")}
          >
            Vencimientos ({paraRenovar.length})
          </button>
          {viejos.length > 0 && (
            <button
              className={"tk-chip" + (vista === "viejos" ? " activo" : "")}
              onClick={() => setVista("viejos")}
            >
              Vencidos hace más de 90 días ({viejos.length})
            </button>
          )}
          <button
            className={"tk-chip" + (vista === "buscar" ? " activo" : "")}
            onClick={() => setVista("buscar")}
          >
            Buscar
          </button>
          {/* La agenda no es otra vista de la misma lista: es lo que se está
              HACIENDO con esa lista. Sin ella los pendientes se miran el lunes,
              se llaman dos plantas, y el lunes siguiente la lista está igual —
              no porque no se haya hecho nada, sino porque lo hecho no se
              anotaba en ningún lado. */}
          <button
            className={"tk-chip" + (vista === "agenda" ? " activo" : "")}
            onClick={() => setVista("agenda")}
          >
          Agenda de turnos
        </button>
      </div>

        {/* El estado, adentro de la solapa. Son dos preguntas: la solapa
            separa lo accionable de lo arqueológico, y esto dice en qué punto
            está cada uno. En la búsqueda no va — ahí se preguntó por un camión
            concreto. */}
        {vista !== "buscar" && (
          <div className="tk-filtros tk-filtros--sub">
            {FILTROS.map(([clave, nombre]) => (
              <button
                key={clave}
                className={"tk-chip" + (porEstado === clave ? " activo" : "")}
                onClick={() => setPorEstado(clave)}
              >
                {nombre} · {cuentas[clave]}
              </button>
            ))}
            {/* EL LÁPIZ, y sólo cuando hay algo que cargar.
                Un botón que no cambia nada en la pantalla —porque las 658
                filas ya tienen fecha del CENT— se aprende a ignorar, que es
                justo lo que pasó con las llaves que no abrían nada. Con el
                número al lado, además, dice cuánto trabajo hay. */}
            {puedeCargar && porCargar > 0 && (
              <button
                className={"tk-chip" + (lapiz ? " activo" : "")}
                onClick={() => setLapiz((v) => !v)}
                title="Los 0 km no rinden RTO por un año: la fecha viene en el título."
              >
                ✏️ Fecha del título · {porCargar}
              </button>
            )}
          </div>
        )}

        {vista === "buscar" && (
          <form onSubmit={buscar} className="tk-responder">
            <input
              className="permisos-buscar"
              type="search"
              placeholder="Patente o interno…"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              aria-label="Buscar camión"
              autoFocus
            />
            <button className="tk-boton">Ver</button>
          </form>
        )}

      <TablaUnidades
        columnas={columnas}
        filas={lista}
        vacio={
          vista === "pendientes"
            ? "No hay ningún tractor cargado."
            : vista === "viejos"
              ? "Ninguno vencido hace más de 90 días."
              : texto
                ? "Ningún camión con esa patente o interno."
                : "Escribí una patente o un interno."
        }
      />
    </div>
  );
};

export default RTO;
